<template>
  <div
    class="BaseFile"
    ref="componentRef"
  >
    <label
      v-if="label"
      :for="id"
    >
      {{ label }}
    </label>

    <v-file-input
      :id="id"
      :aria-label="label || buttonText || t('common.buttons.attach')"
      :aria-describedby="fieldValidationErrorMessage ? id + '-error' : undefined"
      :aria-invalid="!!fieldValidationErrorMessage"
      ref="$field"
      v-model="files"
      :accept="accept"
      variant="outlined"
      :show-size="false"
      :hide-details="true"
      :rules="rules"
      @change="handleFileChange"
      validate-on="submit lazy"
      :disabled="disabled"
    >
      <template v-slot:prepend-inner>
        <i
          class="icon-b-attachment"
          aria-hidden="true"
        />

        <p v-if="buttonText">
          {{ buttonText }}
        </p>
        <p v-else>
          {{ t('common.buttons.attach') }}
        </p>
      </template>

      <template v-slot:selection="{}"></template>
    </v-file-input>

    <div
      :id="`${id}-messages`"
      hidden
    ></div>

    <p
      v-if="footerMessage"
      class="info-message"
    >
      {{ footerMessage }}
    </p>

    <div
      class="file-name"
      v-if="fileName"
      :class="{ 'size-error': !isValid }"
    >
      <div class="file">
        <i
          class="icon-b-document"
          aria-hidden="true"
        ></i>
        <span :title="fileName">{{ fileName }}</span>
      </div>

      <button
        type="button"
        @click="removeFile"
        class="remove-file"
        :aria-label="ARIA_LABELS.REMOVE_FILE + ' ' + fileName"
      >
        <i
          class="icon-b-close"
          aria-hidden="true"
        ></i>
        <span>
          {{ t('common.buttons.delete') }}
        </span>
      </button>
    </div>

    <ErrorMessageComponent
      :messages="fieldValidationErrorMessage"
      :id="id + '-error'"
    />
  </div>
</template>

<script setup lang="ts">
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '@/modules/core/composables/accesibilityComposable'
  import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
  import { ARIA_LABELS } from '@/modules/core/constants/ariaLabels'
  import type { ValidationRuleType } from '@/modules/core/models/types'
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import ErrorMessageComponent from '../formFields/ErrorMessageComponent.vue'

  const { t } = useI18n()

  interface PropsInterface {
    id: string
    label?: string
    buttonText?: string
    modelValue: File[]
    accept?: string
    size?: string
    extensions?: string[]
    footerMessage?: string
    rules?: ValidationRuleType[]
    disabled?: boolean
  }

  const emit = defineEmits(['update:modelValue', 'remove'])

  const props = withDefaults(defineProps<PropsInterface>(), {
    rules: () => [],
    accept: '.pdf,.png,.jpg,.jpeg'
  })

  const validationRules = {
    validExtension: (files: File[]) => {
      const isValid = files.every((f) => validateExtension(extensions.value, f.type))

      return isValid || t('common.validations.file-format')
    },
    validSize: (files: File[]) => {
      const isValidValue = files.every((f) => f.size <= 5000000)

      return isValidValue || t('common.validations.file-size')
    }
  }

  const rules = [...props.rules, validationRules.validExtension, validationRules.validSize]

  const files = ref(props.modelValue)
  const fileName = computed(() =>
    Array.from(files.value)
      .map((f) => f.name)
      .join(',')
  )
  const extensions = computed(() => props.accept.split(','))

  const isValid = computed(isValidFiles)

  function isValidFiles() {
    if (files.value.length > 0) {
      const validExtension = validationRules.validExtension(files.value)
      const validSize = validationRules.validSize(files.value)

      return validExtension === true && validSize === true
    }

    return false
  }

  function validateExtension(extensions: string[], type: string) {
    if (!type) return false

    return extensions.some((e) => type.toLowerCase().includes(e.replace('.', '/').toLowerCase()))
  }

  function handleFileChange(event: any) {
    if (!event.target.files.length) {
      removeFile()
      return
    }

    const uploadedFiles = event.target.files
    files.value = [...uploadedFiles]

    emit('update:modelValue', files.value)

    // Validar campo después de adjuntar nuevo archivo
    if ($field.value) {
      $field.value.validate()
    }
  }

  function removeFile() {
    files.value = []

    emit('update:modelValue', [])
    emit('remove')
  }

  const { $field, fieldValidationErrorMessage } = useValidationError()

  const componentRef = ref<HTMLElement | null>(null)
  let observer: MutationObserver | null = null

  onMounted(() => {
    observer = startDuplicatedLabelControl(componentRef.value)
  })

  onUnmounted(() => {
    endDuplicatedLabelControl(observer)
  })
</script>

<style lang="scss" scoped>
.BaseFile {
  label {
    font-family: $font-family-primary;
    font-size: $font-size-m;
    font-style: normal;
    font-weight: $font-weight-400;
    line-height: $line-height-120p;
    color: $color-neutral-dark;
    display: block;
    margin-bottom: $spacing-s;
  }

  .info-message {
    font-size: $font-size-s;
    font-weight: $font-weight-400;
    color: $color-neutral;
    margin-top: $spacing-s;
  }

  .file-name {
    margin-top: $spacing-l;
    border-bottom: 1px solid $color-neutral-light;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: $spacing-l;

    .file {
      display: flex;
      gap: $gap-s;
      align-items: center;
      font-size: $font-size-l;
      color: $color-neutral-dark;

      span {
        max-width: $size-250;
        overflow: hidden;
        text-overflow: ellipsis;
        text-wrap: nowrap;
      }
    }

    .remove-file {
      color: $color-primary;
      display: flex;
      gap: $gap-m;
      align-items: center;
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;

      span {
        font-weight: $font-weight-700;
        cursor: pointer;
      }

      i {
        font-size: $font-size-xl;
        cursor: pointer;
      }
    }

    &.size-error {
      border-color: $color-error;

      .file {
        color: $color-error;
      }
    }
  }

  .v-icon {
    cursor: pointer;
    color: $color-primary;
  }

  :deep(.v-input__prepend) {
    display: none;
  }

  :deep(.v-input) {
    &:focus-within {
      box-shadow: 0 0 0 4px $color-focus;
    }
  }

  :deep(.v-field) {
    cursor: pointer;
    display: flex;

    .v-field__prepend-inner {
      width: $size-full;
      justify-content: center;
      gap: $gap-m;

      i {
        font-size: $font-size-xl;
      }

      p {
        font-weight: $font-weight-700;
      }
    }

    .v-field__field {
      width: 0;
    }

    .v-field__clearable {
      display: none;
    }
  }

  :deep(.v-field__outline__start) {
    border-color: $color-primary;
    border-radius: $b-radius-2 0 0 $b-radius-2;
    opacity: 1;
  }

  :deep(.v-field__outline__end) {
    border-color: $color-primary;
    border-radius: 0 $b-radius-2 $b-radius-2 0;
    opacity: 1;
  }

  :deep(.v-input__details) {
    padding-left: 0;

    .v-messages__message {
      color: $color-neutral;
      font-size: $font-size-s;
    }
  }

  :deep(.v-field--error:not(.v-field--disabled)) {
    background-color: $color-neutral-00 !important;
  }

  :deep(.v-field--disabled .v-field__overlay) {
    background-color: transparent;
  }
}
</style>
